import type { ColumnType } from 'antd/es/table';

/**
 * Settings for individual table columns
 */
export interface TableColumnSettings {
  dataIndex: string;
  visible: boolean;
  width?: number;
  order: number;
}

/**
 * Pagination settings for a table
 */
export interface TablePaginationSettings {
  pageSize: number;
  current: number;
  showSizeChanger: boolean;
  showQuickJumper: boolean;
  pageSizeOptions: number[];
}

/**
 * Single sort rule for multi-sort
 */
export interface TableSortRule {
  field: string;
  order: 'ascend' | 'descend';
}

/**
 * Sort settings for a table
 */
export interface TableSortSettings {
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC' | 'ascend' | 'descend';
  /** Multi-sort rules (priority order) */
  multiSort?: TableSortRule[];
}

/**
 * Complete settings for a single table
 */
export interface TableSettings {
  columns: {
    [columnKey: string]: TableColumnSettings;
  };
  pagination: TablePaginationSettings;
  sort: TableSortSettings;
  lastUpdated: number;
}

/**
 * Zustand store state for all table settings
 */
export interface TableSettingsState {
  settings: {
    [tableId: string]: TableSettings;
  };

  // Actions
  getTableSettings: (tableId: string) => TableSettings | undefined;
  updateColumnSettings: (
    tableId: string,
    columnKey: string,
    settings: Partial<TableColumnSettings>,
  ) => void;
  updatePaginationSettings: (tableId: string, pagination: Partial<TablePaginationSettings>) => void;
  updateSortSettings: (tableId: string, sort: TableSortSettings) => void;
  updateMultiSort: (tableId: string, multiSort: TableSortRule[]) => void;
  toggleColumnVisibility: (tableId: string, columnKey: string, visible: boolean) => void;
  updateColumnOrder: (tableId: string, columnKey: string, newOrder: number) => void;
  updateColumnWidth: (tableId: string, columnKey: string, width: number) => void;
  resetTableSettings: (tableId: string) => void;
  resetAllSettings: () => void;
  initializeTableSettings: (
    tableId: string,
    columns: ColumnType<any>[],
    defaultPagination?: Partial<TablePaginationSettings>,
  ) => void;
  loadFromAPI: () => Promise<void>;
}

/**
 * Default settings
 */
export const DEFAULT_PAGINATION_SETTINGS: TablePaginationSettings = {
  pageSize: 10,
  current: 1,
  showSizeChanger: true,
  showQuickJumper: true,
  pageSizeOptions: [10, 20, 50, 100],
};

export const DEFAULT_SORT_SETTINGS: TableSortSettings = {
  sortBy: undefined,
  sortOrder: undefined,
  multiSort: [],
};

/**
 * Props for the DataTable component
 */
export interface DataTableProps<T = any> {
  tableId: string; // Required unique ID
  columns: Array<{
    title: string;
    dataIndex?: string | string[];
    key?: string;
    width?: number;
    fixed?: 'left' | 'right';
    render?: (value: unknown, record: T, index: number) => React.ReactNode;
    [key: string]: any;
  }>;
  data: T[];
  loading?: boolean;
  onEdit?: (record: T) => void;
  onDelete?: (record: T) => void;
  showActions?: boolean;
  enableReordering?: boolean; // Default: true
  enableResizing?: boolean; // Default: true
  title?: string;
  rowKey?: string | ((record: T) => string);
  scroll?: { x?: number | string; y?: number | string };
  expandable?: any;
  rowClassName?: string | ((record: T, index: number) => string);
  onRow?: (record: T, index?: number) => React.HTMLAttributes<HTMLElement>;
  [key: string]: any; // Allow all other Ant Design Table props
}
